import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface HistologyWaitCustomerRow {
  id: string;
  label: string;
  provider_id: string;
  histology_wait_days: number | null;
}

// The generated Supabase `Database` type does not yet include customers.histology_wait_days.
// Drop this facade once `pnpm --filter qa-app run sb:fetch-db-types` has been re-run against a
// project where the histology_wait_days migration is applied.
type UntypedResult = { data: unknown; error: { message: string } | null };
interface UntypedSelect {
  order: (col: string) => Promise<UntypedResult>;
}
interface UntypedFilter {
  eq: (col: string, val: string) => Promise<UntypedResult>;
}
interface UntypedQuery {
  select: (cols: string) => UntypedSelect;
  update: (patch: Record<string, unknown>) => UntypedFilter;
}
interface UntypedSupabase {
  from: (table: string) => UntypedQuery;
}
const untyped = supabase as unknown as UntypedSupabase;

export const DEFAULT_HISTOLOGY_WAIT_DAYS = 31;
export const MIN_HISTOLOGY_WAIT_DAYS = 1;
export const MAX_HISTOLOGY_WAIT_DAYS = 365;

/** Parses a number input into a day count, `null` (empty = use the default), or `undefined` (invalid — reject). */
export function parseHistologyWaitDays(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < MIN_HISTOLOGY_WAIT_DAYS || n > MAX_HISTOLOGY_WAIT_DAYS) return undefined;
  return n;
}

function HistologyWaitDaysInput({
  customer,
  disabled,
  onCommit,
}: {
  customer: HistologyWaitCustomerRow;
  disabled: boolean;
  onCommit: (days: number | null) => void;
}) {
  const [value, setValue] = useState<string>(customer.histology_wait_days?.toString() ?? "");

  const commit = () => {
    const parsed = parseHistologyWaitDays(value);
    if (parsed === undefined) {
      toast.error(`Enter a whole number of days between ${MIN_HISTOLOGY_WAIT_DAYS} and ${MAX_HISTOLOGY_WAIT_DAYS}`);
      setValue(customer.histology_wait_days?.toString() ?? "");
      return;
    }
    if (parsed === customer.histology_wait_days) return;
    onCommit(parsed);
  };

  return (
    <Input
      type="number"
      inputMode="numeric"
      min={MIN_HISTOLOGY_WAIT_DAYS}
      max={MAX_HISTOLOGY_WAIT_DAYS}
      value={value}
      disabled={disabled}
      placeholder={`${DEFAULT_HISTOLOGY_WAIT_DAYS} (default)`}
      aria-label={`Histology wait days for ${customer.label}`}
      className="w-32"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

export default function CustomerHistologyWaitSection() {
  const queryClient = useQueryClient();

  const {
    data: customers,
    isError,
    error,
  } = useQuery({
    queryKey: ["customers", "for-histology-wait"],
    queryFn: async () => {
      const { data, error } = await untyped
        .from("customers")
        .select("id,label,provider_id,histology_wait_days")
        .order("label");
      if (error) throw error;
      return (data ?? []) as HistologyWaitCustomerRow[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, days }: { id: string; days: number | null }) => {
      const { error } = await untyped.from("customers").update({ histology_wait_days: days }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", "for-histology-wait"] });
      toast.success("Histology wait saved");
    },
    onError: (error: Error) => toast.error(`Failed to save: ${error.message}`),
  });

  return (
    <div className="space-y-4 border rounded-lg p-6 bg-card">
      <h2 className="text-xl font-semibold">Endoscopy Histology Wait</h2>
      <p className="text-sm text-muted-foreground">
        Number of days to wait for an endoscopy histology report before coding the encounter without it. Leave empty to
        fall back to the default of {DEFAULT_HISTOLOGY_WAIT_DAYS} days.
      </p>

      {isError ? (
        <p className="text-sm text-destructive">Failed to load customers: {error?.message ?? "unknown error"}</p>
      ) : customers === undefined ? (
        <p className="text-sm text-muted-foreground">Loading customers…</p>
      ) : customers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No customers found.</p>
      ) : (
        <ul className="space-y-2">
          {customers.map((customer) => {
            const isSaving = saveMutation.isPending && saveMutation.variables?.id === customer.id;
            return (
              <li
                key={customer.id}
                className="flex items-center justify-between border rounded px-3 py-2 bg-background"
              >
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={cn(
                      "h-2 w-2 rounded-full",
                      customer.histology_wait_days != null ? "bg-primary" : "bg-muted-foreground/30",
                    )}
                  />
                  <span className="font-medium">{customer.label}</span>
                  <span className="text-sm text-muted-foreground">{customer.provider_id}</span>
                </div>
                <div className="flex items-center gap-2" aria-live="polite">
                  {isSaving && <span className="text-sm text-muted-foreground">Saving…</span>}
                  <HistologyWaitDaysInput
                    customer={customer}
                    disabled={isSaving}
                    onCommit={(days) => saveMutation.mutate({ id: customer.id, days })}
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
